import { createRouter, createWebHistory } from 'vue-router'
import VueLayout from '../components/Layout.vue'
import VueHome from '../views/Home.vue'
import VueInfo from '../views/Info.vue'
import VueTours from '../views/Tours.vue'
import VuePoints from '../views/Points.vue'
import VueNotFound from '../views/NotFound.vue'
import PointMismatch from '../views/PointMismatch.vue'
import PointSelection from "../views/PointSelection.vue"
import Qrcode from "../views/Qrcode.vue"
import store from '../store'
import i18n from '../i18n'
import api from '../services/api'
import { hasAuthenticatedSession } from '../services/authSession'


// Language handling via query (?lang=...)
const loadLanguage = async (to) => {
    console.log("=== ROUTER LANGUAGE DETECTION ===");
    console.log("Route params:", to.params);
    console.log("Route query:", to.query);
    console.log("Current i18n locale:", i18n.locale);
    console.log("Current URL:", window.location.pathname + window.location.search);

    const queryLang = to.query?.lang

    // Determine desired language
    let resolvedLang = queryLang || i18n.locale || (navigator.language ? navigator.language.split('-')[0] : 'en') || 'en'

    // Sync i18n
    if (resolvedLang && i18n.locale !== undefined && i18n.locale !== resolvedLang) {
        console.log("Update Vue i18n language from", i18n.locale, "to", resolvedLang);
        i18n.locale = resolvedLang
    }

    const needsNormalize = !queryLang
    console.log("After detection - i18n locale:", i18n.locale);
    console.log("needsNormalize:", needsNormalize);
    console.log("=== END LANGUAGE DETECTION ===");
    return { resolvedLang, needsNormalize }
}
const reservedFirstSegments = new Set(['qr', 'point', 'tours', 'info', 'list', 'points', 'not-found'])

const routes = [
    // Root homepage: try to detect active tour; if none, show Home
    {
        path: "/",
        component: VueLayout,
        beforeEnter: async (to) => {
            const { resolvedLang, needsNormalize } = await loadLanguage(to)
            if (needsNormalize) return { path: '/', query: { ...to.query, lang: resolvedLang }, replace: true }
            // Normalize redirectTo when it contains a tourId path at root level
            // Example: /?redirectTo=/NZx9/points&lang=en  ->  /NZx9/?redirectTo=/points&lang=en
            const redirectTo = to.query?.redirectTo
            if (redirectTo && typeof redirectTo === 'string' && redirectTo.startsWith('/')) {
                try {
                    const parts = redirectTo.split('/').filter(Boolean) // ['', 'NZx9', 'points'] -> ['NZx9','points']
                    // check reserved names
                    if (parts.length >= 1 && !reservedFirstSegments.has(parts[0])) {
                        const candidateTourId = parts[0]
                        // Only rewrite when redirectTo clearly encodes a tour path like /:tourId/...
                        if (candidateTourId) {
                            const trimmedPath = '/' + parts.slice(1).join('/') // '/points' (or '/')
                            return {
                                path: `/${candidateTourId}/`,
                                query: { ...to.query, redirectTo: trimmedPath },
                                replace: true
                            }
                        }
                    }
                } catch (e) {
                    // fall through if parsing fails
                }
            }
            console.log('resetting activeTour before loading tour settings')
            await store.dispatch('tours/resetActiveTour')
            return true
        },
        children: [
            {
                path: '',
                name: 'root-home',
                component: VueHome,
                beforeEnter: async (to) => {
                    const { resolvedLang, needsNormalize } = await loadLanguage(to)
                    if (needsNormalize) return { name: 'root-home', params: to.params, query: { ...to.query, lang: resolvedLang }, replace: true }
                    return true
                }
            },

        ]
    },
    // Normalize tour path: add trailing slash
    {
        path: "/:tourId",
        redirect: to => ({ path: `/${to.params.tourId}/`, query: to.query })
    },

    // QR code route (language via ?lang)
    { 
        path: "/qr/:qrcode", 
        name: "qrcode", 
        component: Qrcode,
        beforeEnter: async (to) => {
            const { resolvedLang, needsNormalize } = await loadLanguage(to)
            if (needsNormalize) {
                return { name: 'qrcode', params: to.params, query: { ...to.query, lang: resolvedLang }, replace: true }
            }
            return true
        }
    },
    // Top-level not-found route (no tourId required)
    {
        path: "/not-found",
        name: "not-found",
        component: VueNotFound,
        beforeEnter: async (to) => {
            const { resolvedLang, needsNormalize } = await loadLanguage(to)
            if (needsNormalize) return { name: 'not-found', params: to.params, query: { ...to.query, lang: resolvedLang }, replace: true }
            return true
        }
    },


    
  // Pre-selection route for points (language via ?lang)
  {
    path: "/point/:pointId",
    name: "point-selection",
    component: PointSelection,
    beforeEnter: async (to) => {
      const { resolvedLang, needsNormalize } = await loadLanguage(to)
      if (needsNormalize) {
        return { name: 'point-selection', params: to.params, query: { ...to.query, lang: resolvedLang }, replace: true }
      }

      // Fetch available tours for this point and decide where to go
      try {
        const response = await api.getPoint(to.params.pointId)
        const tours = [...new Map(
          ((response.data?.tour_points || []).map(tp => tp.tour) || []).map(t => [t.id, t])
        ).values()]

        const activeTour = store.getters['tours/activeTour']

        // If active tour is set and available for this point, go straight to that tour's point
        if (activeTour && tours.some(t => String(t.id) === String(activeTour))) {
          return { path: `/${activeTour}/point/${to.params.pointId}` , query: to.query, replace: true }
        }

        // If active tour is set but NOT available for this point, show point-mismatch page
        if (activeTour && !tours.some(t => String(t.id) === String(activeTour))) {
          return { name: 'point-mismatch', params: {tourId: activeTour}, query: { ...to.query } }
        }

        if (tours.length === 1) {
          const onlyTourId = tours[0].id
          await store.dispatch('tours/setActiveTour', onlyTourId)
          return { path: `/${onlyTourId}/point/${to.params.pointId}`, query: to.query, replace: true }
        }

        // Otherwise render the selection component
        return true
      } catch (e) {
        console.error('Failed to load tours for point:', e)
        return { name: 'not-found' }
      }
    }
  },

     // Tour-specific routes: /:tourId/ (language via ?lang)
    {
        path: "/:tourId/", // we need the trailing slash to avoid issues with redirect loops    
        component: VueLayout,
        beforeEnter: async (to) => {
            console.log('=== ROUTER BEFORE ENTER ===')
            console.log('Route params:', to.params)
            console.log('Current route name:', to.name)
            console.log('TourId from route:', to.params.tourId)

            // Early disambiguation: if first segment is a reserved keyword, redirect to its own route
            if (reservedFirstSegments.has(to.params.tourId)) {
                const seg = to.params.tourId
                const parts = to.path.split('/').filter(Boolean)
                const second = parts[1]
                if (seg === 'qr' && second) {
                    return { name: 'qrcode', params: { qrcode: second }, query: to.query, replace: true }
                }
                if (seg === 'point' && second) {
                    return { name: 'point-selection', params: { pointId: second }, query: to.query, replace: true }
                }
                // Fallback: redirect to route name if defined, otherwise to root of that feature
                if (seg === 'tours') return { name: 'tours' }
                if (seg === 'info') return { name: 'info' }
                if (seg === 'list') return { name: 'list' }
                if (seg === 'points') return { name: 'home' }
                if (seg === 'not-found') return { path: '/not-found' }
                return { path: `/${seg}` }
            }

            // Code-protected tours require a valid bearer-token session.
            const settingsResponse = await api.getTourSettings(to.params.tourId)
            const baseSettings = settingsResponse.data
            const hasAuthSession = hasAuthenticatedSession()
            console.log('Has authenticated session:', hasAuthSession)
            console.log('Code required:', Number(baseSettings.access?.is_code_required))
            if (Number(baseSettings.access?.is_code_required) > 0 && !hasAuthSession) {
                console.log('Code required.')
                // Preserve intended destination (relative to /:tourId/) so Home can redirect after code entry
                // Example: /NZx9/point/EAGE?lang=nl -> redirectTo=/point/EAGE
                let redirectTo = null
                try {
                    const parts = to.path.split('/').filter(Boolean) // ['NZx9','point','EAGE']
                    if (parts.length > 1) {
                        redirectTo = '/' + parts.slice(1).join('/')
                    }
                } catch (e) {
                    // ignore parsing errors; fallback to no redirectTo
                }

                const nextQuery = redirectTo ? { ...to.query, redirectTo } : { ...to.query }

                console.log('Redirecting to home with params:', to.params)
                console.log('Redirecting to home with query:', nextQuery)

                if (to.name !== 'home') {
                    return { name: 'home', params: to.params, query: nextQuery, replace: true }
                } else {
                    console.log('No redirect to home, we already are on the home page with params:', to.params)
                }
            } else {
                console.log('No code required.')
            }


            const { resolvedLang, needsNormalize } = await loadLanguage(to)
            if (needsNormalize) {
                return { path: to.path, query: { ...to.query, lang: resolvedLang }, replace: true }
            }
            
            // Resolve optional/alias tourId when single/default exists or 'tour' alias provided
            let resolvedTourId = to.params.tourId
            const storedTour = localStorage.getItem('activeTour')
            
            if (!resolvedTourId || resolvedTourId === 'tour') {
                if (storedTour) {
                    resolvedTourId = storedTour
                } else {
                    let singleTourId = null
                    try {
                       singleTourId = baseSettings?.tour?.id || (Array.isArray(baseSettings?.tours) && baseSettings.tours.length === 1 ? baseSettings.tours[0]?.id : null)
                    } catch (e) {
                        console.warn('Could not load base settings before resolving tour:', e)
                    }
                    if (singleTourId) {
                        resolvedTourId = singleTourId
                    }
                }
            }
            
            if (resolvedTourId && resolvedTourId !== to.params.tourId) {
                await store.dispatch('tours/setActiveTour', resolvedTourId)
                return { path: `/${resolvedTourId}/`, query: to.query, replace: true }
            }
            
            // Do not mutate store activeTour here; settings and UI are driven by route params
            console.log('Using tourId from route (no store mutation):', to.params.tourId)
            
            // Load tour settings with automatic tour ID detection
            console.log('Loading tour settings with params:', to.params)
            const settings = await store.dispatch('settings/loadTourSettingsAuto', {
                i18n,
                params: to.params
            })
            // If no tour settings could be loaded (no tour detected), show Tours to select one
            if (!settings) {
                return { name: 'tours', query: to.query, replace: true }
            }

            console.log('Tour settings loaded in router:', settings)
            return true
        },
        children: [
            {
                path: "",
                name: "home",
                component: VueHome,
                beforeEnter: async (to) => {
                    const { resolvedLang, needsNormalize } = await loadLanguage(to)
                    if (needsNormalize) return { path: to.path, query: { ...to.query, lang: resolvedLang }, replace: true }
                    return true
                }
            },
            {
                path: "info",
                name: "info",
                component: VueInfo,
                beforeEnter: async (to) => {
                    const { resolvedLang, needsNormalize } = await loadLanguage(to)
                    if (needsNormalize) return { name: 'info', params: to.params, query: { ...to.query, lang: resolvedLang }, replace: true }
                    return true
                }
            },
            {
                path: "tours",
                name: "tours",
                component: VueTours,
                beforeEnter: async (to) => {
                    const { resolvedLang, needsNormalize } = await loadLanguage(to)
                    if (needsNormalize) return { name: 'tours', params: to.params, query: { ...to.query, lang: resolvedLang }, replace: true }
                    return true
                }
            },
            {
                path: "list",
                name: "list",
                component: VuePoints,
                props: { selectedView: 'list' },
                beforeEnter: async (to) => {
                    const { resolvedLang, needsNormalize } = await loadLanguage(to)
                    if (needsNormalize) return { name: 'list', params: to.params, query: { ...to.query, lang: resolvedLang }, replace: true }
                    return true
                }
            },
            {
                path: "points",
                name: "points",
                component: VuePoints,
                beforeEnter: async (to) => {
                    const { resolvedLang, needsNormalize } = await loadLanguage(to)
                    if (needsNormalize) return { name: 'points', params: to.params, query: { ...to.query, lang: resolvedLang }, replace: true }
                    return true
                }
            },
            {
                path: "point/:pointId",
                name: "point",
                component: VuePoints,
                props: true,
                beforeEnter: async (to) => {
                    const { resolvedLang, needsNormalize } = await loadLanguage(to)
                    if (needsNormalize) return { name: 'point', params: to.params, query: { ...to.query, lang: resolvedLang }, replace: true }
                    return true
                },
                children: [
                    {
                        path: "page/:page",
                        name: "point-page",
                        component: VuePoints,
                        props: true
                    }
                ]
            },
            {
                path: "not-found",
                name: "tour-not-found",
                component: VueNotFound,
                beforeEnter: async (to) => {
                    const { resolvedLang, needsNormalize } = await loadLanguage(to)
                    if (needsNormalize) return { name: 'tour-not-found', params: to.params, query: { ...to.query, lang: resolvedLang }, replace: true }
                    return true
                }
            }, {
                path: "point-error",
                name: "point-mismatch",
                component: PointMismatch,
                beforeEnter: async (to) => {
                    const { resolvedLang, needsNormalize } = await loadLanguage(to)
                    if (needsNormalize) return { name: 'point-mismatch', params: to.params, query: { ...to.query, lang: resolvedLang }, replace: true }
                    return true
                }
                
            }
        ]
    }
]

// Redirect any legacy /:language/... path to homepage (no backward compatibility)
routes.push({
    path: "/:first/:rest(.*)",
    beforeEnter: (to) => {
        const langs = new Set(['en','nl','de','fr','fy'])
        if (langs.has(to.params.first)) {
            return { path: '/' }
        }
        return true
    }
})

const router = createRouter({
    history: createWebHistory(),
    routes
})

export default router 